import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { setDefaultLandingPageWhenEmpty } from '@utils/setDefaultLandingPage';

/**
 * Types for the default landing page store
 */
export interface DefaultLandingPage {
  url: string;
  name: string;
  path: string;
  isFromCustomerDefault?: boolean;
}

interface DefaultLandingPageState {
  // Landing page data
  url: string;
  name: string;
  path: string;
  customerId: string | null;
  isFromCustomerDefault: boolean;
}

interface DefaultLandingPageActions {
  setDefaultLandingPage: (payload: Partial<DefaultLandingPage>) => void;
  updateDefaultLandingPage: (payload: Partial<DefaultLandingPage>) => void;
  setCustomerId: (id: string | null) => void;
  reset: () => void;
}

interface PromptState {
  showPrompt: boolean;
  promptTitle: string;
  promptContent: string;
  promptNeedInput: boolean;
  promptOkFn: () => void;
  tmpPayload: Record<string, any>;
}

interface PromptActions {
  setShowPrompt: (show: boolean) => void;
  openPrompt: () => void;
  closePrompt: () => void;
  setPromptState: (state: Partial<PromptState>) => void;
  resetPrompt: () => void;
}

const initialDefaultLandingPageState: DefaultLandingPageState = {
  url: '',
  name: '',
  path: '',
  customerId: null,
  isFromCustomerDefault: false,
};

const initialPromptState: PromptState = {
  showPrompt: false,
  promptTitle: '',
  promptContent: '',
  promptNeedInput: false,
  promptOkFn: () => undefined,
  tmpPayload: {},
};

export const useDefaultLandingPageStore = create<
  DefaultLandingPageState & DefaultLandingPageActions
>()(
  devtools(
    (set) => ({
      ...initialDefaultLandingPageState,

      setDefaultLandingPage: (payload) =>
        set((state) => {
          const processedPayload = setDefaultLandingPageWhenEmpty(
            {
              url: state.url,
              name: state.name,
              path: state.path,
              ...payload,
            },
            state.customerId,
          );

          return {
            url: processedPayload.url,
            name: processedPayload.name,
            path: processedPayload.path,
          };
        }),

      updateDefaultLandingPage: (payload) =>
        set((state) => {
          const processedPayload = setDefaultLandingPageWhenEmpty(
            {
              url: state.url,
              name: state.name,
              path: state.path,
              ...payload,
            },
            state.customerId,
          );

          return {
            url: processedPayload.url,
            name: processedPayload.name,
            path: processedPayload.path,
            isFromCustomerDefault: payload.isFromCustomerDefault ?? state.isFromCustomerDefault,
          };
        }),

      setCustomerId: (id) => set({ customerId: id }),
      reset: () => set(initialDefaultLandingPageState),
    }),
    { name: 'defaultLandingPage-store' },
  ),
);

export const usePromptStore = create<PromptState & PromptActions>()(
  devtools(
    (set) => ({
      ...initialPromptState,

      setShowPrompt: (show) => set({ showPrompt: show }),
      openPrompt: () => set({ showPrompt: true }),
      closePrompt: () => set({ showPrompt: false }),
      setPromptState: (state) => set(state),
      resetPrompt: () => set(initialPromptState),
    }),
    { name: 'prompt-store' },
  ),
);

/**
 * Selector for getting path with /app prefix (similar to defaultPathSelector in Redux)
 */
export const getDefaultPath = (path: string) => (path.startsWith('/app') ? path : `/app${path}`);
